// src/components/common/table/StandardTableFrame.tsx

import React, { ReactNode } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import type { GetRowStatus, TableRowStatus } from './rowStatus';
import { getRowStatusSx } from './rowStatus';
import { standardTableContainerSx, standardTableHeadSx } from './tableStyles';
import { sx, type SxItem } from './sx';

export type LoadingVariant = 'skeleton' | 'centerText' | 'none';

export type StandardTableFrameProps<T> = {
  head: ReactNode;

  rows: T[];
  getRowKey: (row: T, index: number) => string | number;
  renderRow: (row: T, index: number) => ReactNode;

  columnsCount: number;

  size?: 'small' | 'medium';
  stickyHeader?: boolean;
  minWidth?: number;
  maxHeight?: number;

  isLoading?: boolean;
  loadingVariant?: LoadingVariant;
  loadingText?: ReactNode;
  skeletonRows?: number;

  emptyText?: ReactNode;

  getRowStatus?: GetRowStatus<T>;

  /** IMPORTANT: use SxItem (not SxProps) to keep typings stable */
  containerSx?: SxItem;
  tableSx?: SxItem;
  headSx?: SxItem;
};

export function StandardTableFrame<T>({
  head,
  rows,
  getRowKey,
  renderRow,
  columnsCount,

  size = 'medium',
  stickyHeader = true,
  minWidth,
  maxHeight,

  isLoading = false,
  loadingVariant = 'skeleton',
  loadingText = 'Loading...',
  skeletonRows = 6,

  emptyText = 'No data found.',

  getRowStatus,

  containerSx,
  tableSx,
  headSx,
}: StandardTableFrameProps<T>) {
  const hasRows = (rows?.length ?? 0) > 0;

  const getStatus = (row: T, index: number): TableRowStatus =>
    getRowStatus?.(row, index) ?? 'normal';

  const showSkeleton = isLoading && loadingVariant === 'skeleton';
  const showCenterText = isLoading && loadingVariant === 'centerText';
  const showEmpty = !isLoading && !hasRows;

  return (
    <TableContainer
      component={Paper}
      sx={sx(
        standardTableContainerSx,
        maxHeight ? { maxHeight } : null,
        containerSx ?? null,
      )}
    >
      <Table
        stickyHeader={stickyHeader}
        size={size}
        sx={sx(minWidth ? { minWidth } : null, tableSx ?? null)}
      >
        <TableHead sx={sx(standardTableHeadSx, headSx ?? null)}>{head}</TableHead>

        <TableBody>
          {/* Loading */}
          {showSkeleton &&
            Array.from({ length: skeletonRows }).map((_, i) => (
              <TableRow key={`skeleton-${i}`}>
                <TableCell colSpan={columnsCount}>
                  <Skeleton variant="text" />
                </TableCell>
              </TableRow>
            ))}

          {showCenterText && (
            <TableRow>
              <TableCell colSpan={columnsCount}>
                <Box py={4} textAlign="center">
                  {loadingText}
                </Box>
              </TableCell>
            </TableRow>
          )}

          {/* Empty */}
          {showEmpty && (
            <TableRow>
              <TableCell colSpan={columnsCount}>
                <Box py={4} textAlign="center" color="text.secondary">
                  {emptyText}
                </Box>
              </TableCell>
            </TableRow>
          )}

          {/* Rows */}
          {!isLoading &&
            rows.map((row, index) => (
              <TableRow
                key={getRowKey(row, index)}
                hover
                sx={sx(
                  (theme) => ({
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }),
                  getRowStatusSx(getStatus(row, index)),
                )}
              >
                {renderRow(row, index)}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
